/**
 * Connect POS — Impresión por Bluetooth (Web Bluetooth API) para impresoras
 * térmicas 58/80mm ESC/POS (Xprinter, Munbyn, Fremort, POS-5805, POS-58, etc.).
 *
 * Cómo funciona:
 *  1) `pairPrinter()` abre el diálogo nativo de emparejamiento del navegador.
 *  2) Recorre los `primaryServices` hasta encontrar una característica escribible.
 *  3) Guarda el ID de dispositivo en `localStorage` (reconexión silenciosa en Chrome 105+).
 *  4) `printTicket(...)` construye ESC/POS con ancho seguro (58mm = 28 columnas,
 *     ajuste de palabra sin cortes) y QR como mapa de bits `GS v 0` (universal).
 *
 * Soporte: Chrome/Edge Android, Chrome Desktop. NO iOS Safari.
 */

import qrcode from "qrcode-generator";
import { groupTicketPlays, playLabel } from "@/lib/ticketFormat";

const LS_KEY = "connect-bt-printer-id";

// UUIDs comunes de impresoras 58mm baratas. Se declaran en `optionalServices`
// para que Chrome nos permita descubrirlos tras `requestDevice`.
const KNOWN_SERVICES = [
    0x18f0, // Xprinter/Munbyn "SPP-over-BLE" — muy común
    0xff00, // Genérico chino
    0xff10,
    0xff12,
    0xffe0, // HM-10 y similares
    "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
    "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip Serial Port
    "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
];

export function isWebBluetoothAvailable() {
    return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

// ---------- Geometría del papel ----------
// 58mm: 384 dots útiles; Font A (12 dots) da 32 col. Se usan 28 por seguridad en
// impresoras portátiles económicas. 80mm: 576 dots / 48 col.
export const PAPER = {
    "58mm": { cols: 28, dots: 384 },
    "80mm": { cols: 48, dots: 576 },
};
const QR_MAX_DOTS = 180; // QR rasterizado ~140–180 px

// ---------- ESC/POS byte builder ----------

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

// Elimina acentos/ñ para evitar problemas de code page en impresoras baratas.
function ascii(str) {
    return String(str ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ñ/g, "n").replace(/Ñ/g, "N")
        .replace(/[^\x20-\x7e]/g, " ");
}

// Parte un token sin espacios (URL/serial) preferentemente tras '/', '-', '.', '?', '='.
function splitToken(tok, w) {
    const out = [];
    let rest = tok;
    while (rest.length > w) {
        let cut = -1;
        for (let i = w - 1; i >= Math.floor(w / 2); i--) {
            if ("/-.?=&".includes(rest[i])) { cut = i + 1; break; }
        }
        if (cut < 0) cut = w;
        out.push(rest.slice(0, cut));
        rest = rest.slice(cut);
    }
    out.push(rest);
    return out;
}

// Ajuste de palabra: nunca corta palabras que caben en una línea.
export function wrapText(str, w) {
    const words = ascii(str).split(" ");
    const lines = [];
    let cur = "";
    for (const word of words) {
        const parts = word.length > w ? splitToken(word, w) : [word];
        for (const part of parts) {
            if (!cur) cur = part;
            else if (cur.length + 1 + part.length <= w) cur += " " + part;
            else { lines.push(cur); cur = part; }
        }
    }
    lines.push(cur);
    return lines;
}

// Divide un código en líneas equilibradas (ej. serial de 36 → 2×18) sin partir guiones si es posible.
function balancedChunks(code, w) {
    if (code.length <= w) return [code];
    const n = Math.ceil(code.length / w);
    const size = Math.ceil(code.length / n);
    const out = [];
    for (let i = 0; i < code.length; i += size) out.push(code.slice(i, i + size));
    return out;
}

class EscPos {
    constructor(paperSize = "58mm") {
        const g = PAPER[paperSize] || PAPER["58mm"];
        this.cols = g.cols;
        this.dots = g.dots;
        this.dw = false;
        this.parts = [];
    }
    write(...bytes) { this.parts.push(new Uint8Array(bytes)); return this; }
    writeBytes(arr) { this.parts.push(arr instanceof Uint8Array ? arr : new Uint8Array(arr)); return this; }
    raw(s) { this.parts.push(new TextEncoder().encode(s)); return this; }
    width() { return this.dw ? Math.floor(this.cols / 2) : this.cols; }
    // Línea con ajuste de palabra al ancho vigente (respeta doble ancho).
    line(s = "") {
        for (const l of wrapText(s, this.width())) this.raw(l + "\n");
        return this;
    }
    // Etiqueta/valor en una línea; si no caben, etiqueta arriba y valor alineado a la derecha debajo.
    pair(left, right) {
        const w = this.width();
        const l = ascii(left), r = ascii(right);
        if (l.length + r.length + 1 <= w) return this.line(padPair(l, r, w));
        this.line(l);
        for (const seg of wrapText(r, w)) this.raw(seg.padStart(w) + "\n");
        return this;
    }
    ln(n = 1) { for (let i = 0; i < n; i++) this.write(LF); return this; }

    // ESC @ + GS L 0 0 (sin margen izq.) + Font A + tamaño normal.
    init() {
        this.write(ESC, 0x40);
        this.write(GS, 0x4c, 0x00, 0x00);
        this.write(ESC, 0x4d, 0x00);
        this.style({});
        return this;
    }
    alignLeft() { return this.write(ESC, 0x61, 0); }
    alignCenter() { return this.write(ESC, 0x61, 1); }

    // ESC ! n (bold/doble alto/doble ancho) + GS ! n (tamaño) para clones que ignoran uno de los dos.
    style({ bold = false, dwidth = false, dheight = false } = {}) {
        let n = 0;
        if (bold) n |= 0x08;
        if (dheight) n |= 0x10;
        if (dwidth) n |= 0x20;
        this.dw = dwidth;
        this.write(ESC, 0x21, n);
        this.write(GS, 0x21, (dwidth ? 0x10 : 0) | (dheight ? 0x01 : 0));
        return this;
    }

    // Serial/código: doble ancho si cabe; si no, negrita normal en líneas equilibradas.
    code(s) {
        const c = ascii(s).trim();
        if (c.length <= Math.floor(this.cols / 2)) {
            this.style({ bold: true, dwidth: true }).raw(c + "\n");
        } else {
            this.style({ bold: true });
            for (const chunk of balancedChunks(c, this.cols)) this.raw(chunk + "\n");
        }
        return this.style({});
    }

    // QR como mapa de bits monocromo `GS v 0` (no depende de `GS ( k`).
    qr(data) {
        const q = qrcode(0, "M");
        q.addData(data);
        q.make();
        const n = q.getModuleCount();
        const quiet = 2; // el papel en blanco alrededor completa la zona de silencio
        const scale = Math.max(2, Math.floor(QR_MAX_DOTS / (n + quiet * 2)));
        const size = (n + quiet * 2) * scale;
        const bytesPerRow = this.dots / 8;
        const offsetX = Math.floor((this.dots - size) / 2);
        const BAND = 32; // filas por bloque: evita desbordar el buffer de impresoras baratas
        this.alignLeft().ln(1);
        for (let y0 = 0; y0 < size; y0 += BAND) {
            const rows = Math.min(BAND, size - y0);
            const buf = new Uint8Array(8 + bytesPerRow * rows);
            buf.set([GS, 0x76, 0x30, 0x00, bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff, rows & 0xff, (rows >> 8) & 0xff]);
            for (let r = 0; r < rows; r++) {
                const my = Math.floor((y0 + r) / scale) - quiet;
                for (let x = 0; x < size; x++) {
                    const mx = Math.floor(x / scale) - quiet;
                    if (my >= 0 && my < n && mx >= 0 && mx < n && q.isDark(my, mx)) {
                        const px = offsetX + x;
                        buf[8 + r * bytesPerRow + (px >> 3)] |= 0x80 >> (px & 7);
                    }
                }
            }
            this.writeBytes(buf);
        }
        return this;
    }

    build() {
        let total = 0;
        for (const p of this.parts) total += p.length;
        const out = new Uint8Array(total);
        let off = 0;
        for (const p of this.parts) { out.set(p, off); off += p.length; }
        return out;
    }
}

function padPair(left, right, width) {
    left = ascii(left); right = ascii(right);
    const maxLeft = Math.max(0, width - right.length - 1);
    if (left.length > maxLeft) left = left.slice(0, maxLeft);
    return left + " ".repeat(Math.max(1, width - left.length - right.length)) + right;
}

function formatDateVE(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = -4 * 60 * 60 * 1000;
    const local = new Date(d.getTime() + off);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(local.getUTCDate())}/${pad(local.getUTCMonth() + 1)}/${local.getUTCFullYear()} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
}
function formatTimeVE(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = -4 * 60 * 60 * 1000;
    const local = new Date(d.getTime() + off);
    return `${String(local.getUTCHours()).padStart(2, "0")}:${String(local.getUTCMinutes()).padStart(2, "0")}`;
}
function fmtMoney(n) {
    const v = Number(n || 0);
    return v.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtUsd(n) {
    return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtCop(n) {
    return Number(Math.round(Number(n || 0))).toLocaleString("es-CO", { maximumFractionDigits: 0 });
}

// Cabecera común CONNECT + subtítulos.
function header(p, subtitles = []) {
    p.alignCenter().style({ bold: true, dwidth: true, dheight: true }).line("CONNECT");
    p.style({}).line("SISTEMA DE JUEGOS");
    for (const s of subtitles) if (s) p.line(s);
}

// QR + URL legible (envuelta sin cortes arbitrarios).
function qrBlock(p, url) {
    p.qr(url);
    p.alignCenter().ln(1).line(url.replace(/^https?:\/\//, ""));
}

// Pie legal pre-envuelto a ≤28 columnas (58mm) con ajuste de palabra limpio; compartido con la vista navegador.
export const TICKET_LEGAL_LINES = [
    "Conserve este ticket para", "reclamar su premio.",
    "Válido solo presentando el", "serial o el QR.",
    "Premios pagaderos en la", "taquilla emisora.",
    "Jugadas válidas solo para", "sorteos del día.",
];
export const TICKET_END_MARK = "--- FIN DEL TICKET ---";
// Buffer anti-corte: líneas en blanco incondicionales tras el pie antes del corte (cuchilla/rasgado manual).
export const MIN_SAFETY_FEED_LINES = 4;

function footer(p, lines, feedLines, cutPaper) {
    p.alignCenter();
    for (const l of lines) p.line(l);
    p.ln(1).line(TICKET_END_MARK);
    p.ln(Math.max(MIN_SAFETY_FEED_LINES, Number(feedLines) || 0));
    if (cutPaper) p.write(GS, 0x56, 66, 0); // GS V 66 0 = feed + corte parcial
}

/**
 * Construye la secuencia ESC/POS del ticket.
 * @param {object} ticket - TicketOut del backend
 * @param {object} ctx - { lotteryMap, sorteoMap, isReprint, operatorName, validationUrl, paperSize, feedLines, cutPaper }
 */
export function buildTicketBytes(ticket, ctx = {}) {
    const {
        lotteryMap = {}, sorteoMap = {}, isReprint = false, operatorName = "", taquillaName = "", validationUrl = "",
        paperSize = "58mm", feedLines = 3, cutPaper = true,
    } = ctx;
    // Aislamiento estricto: solo la divisa de emisión, sin tasas ni equivalencias.
    const cur = ticket.currency || "BS";
    const bsBase = Number(ticket.total_bs) || 0;
    const ratio = cur === "BS" || bsBase <= 0 ? 1 : (cur === "COP" ? Number(ticket.total_cop) : Number(ticket.total_usd)) / bsBase;
    const amt = (bs) => {
        const v = Number(bs || 0) * ratio;
        return cur === "COP" ? fmtCop(Math.round(v)) : cur === "USD" ? fmtUsd(v) : fmtMoney(v);
    };
    const sym = cur === "BS" ? "Bs " : "$ ";
    const p = new EscPos(paperSize).init();
    const W = p.cols;
    const HR = "-".repeat(W);

    header(p, [taquillaName && `Taquilla: ${taquillaName}`, operatorName && `Cajero: ${operatorName}`]);
    if (isReprint) p.style({ bold: true }).line("*** REIMPRESION / COPIA ***").style({});
    p.line(formatDateVE(ticket.created_at));
    p.line(HR);

    p.line("SERIAL");
    p.code(ticket.code);
    p.line(HR);

    // Jugadas — agrupadas por jugada idéntica (número/signo/monto) en varios sorteos
    p.alignLeft();
    p.line(`JUGADAS (${ticket.plays.length})`);
    p.line(HR);
    for (const g of groupTicketPlays(ticket.plays, lotteryMap, sorteoMap)) {
        const multi = g.count > 1;
        for (const e of g.entries) {
            const hora = e.scheduled_at ? formatTimeVE(e.scheduled_at) : "";
            const name = ascii(e.lottery_name || "-");
            // Hora siempre visible a la derecha; el nombre se recorta si no cabe.
            p.line(hora ? padPair(name, hora, W) : name.slice(0, W));
        }
        const label = playLabel(g) + (multi ? " X" + amt(g.amount_bs) : "");
        const right = sym + amt(multi ? g.total_bs : g.amount_bs);
        p.style({ bold: true }).pair(label, right).style({});
    }
    p.line(HR);

    const totalLine = cur === "COP"
        ? ["TOTAL COP", "$ " + fmtCop(ticket.total_cop)]
        : cur === "USD"
            ? ["TOTAL USD", "$ " + fmtUsd(ticket.total_usd)]
            : ["TOTAL Bs", fmtMoney(ticket.total_bs)];
    p.style({ bold: true, dheight: true }).pair(totalLine[0], totalLine[1]).style({});
    p.line(HR);

    if (validationUrl) qrBlock(p, validationUrl);

    footer(p, ["", ...TICKET_LEGAL_LINES], feedLines, cutPaper);
    return p.build();
}

// ---------- Formato del comprobante de RECARGA 58/80mm ----------

/**
 * Construye la secuencia ESC/POS de un comprobante de recarga/servicio Ekiipago.
 * @param {object} rec - transacción de recarga (respuesta de /ekiipago/recarga)
 * @param {object} ctx - { operatorName, paperSize, feedLines, cutPaper }
 */
export function buildRechargeBytes(rec, ctx = {}) {
    const { operatorName = "", paperSize = "58mm", feedLines = 3, cutPaper = true } = ctx;
    const p = new EscPos(paperSize).init();
    const W = p.cols;
    const HR = "-".repeat(W);
    const approved = (rec.status === "approved");

    header(p, []);
    p.style({ bold: true }).line("RECARGA DE SERVICIOS").style({});
    if (operatorName) p.line(operatorName);
    p.line(formatDateVE(rec.created_at));
    p.line(HR);

    p.alignLeft();
    p.style({ bold: true }).pair("OPERADORA", rec.provider_name || "-").style({});
    p.pair("Cuenta", rec.cuenta || "-");
    p.line(HR);
    p.style({ bold: true, dheight: true }).pair("MONTO Bs", fmtMoney(rec.monto_bs)).style({});
    p.line(HR);
    p.pair("N Aprob.", rec.approval_number || "-");
    p.pair("Ref.", rec.reference || "-");
    p.pair("Estatus", approved ? "APROBADA" : String(rec.status || "").toUpperCase());
    p.line(HR);

    footer(p, ["", approved ? "Recarga procesada" : "Recarga NO procesada", "Conserve este comprobante"], feedLines, cutPaper);
    return p.build();
}

/** Imprime un comprobante de recarga por Bluetooth (reconecta o empareja). */
export async function printRechargeViaBluetooth(rec, ctx = {}) {
    try {
        const conn = await getPrinterConnection();
        const bytes = buildRechargeBytes(rec, ctx);
        await writeChunks(conn.characteristic, bytes);
        return { ok: true, device: conn.device };
    } catch (e) {
        return { ok: false, error: e?.message || String(e) };
    }
}

// ---------- Formato del comprobante de SEGURO (Venta Masiva) 58/80mm ----------

/**
 * Construye la secuencia ESC/POS de un comprobante de Venta Masiva de Seguros
 * Pirámide: marca CONNECT, producto, monto (Bs/USD), serial único de activación
 * y QR/URL de canje en el portal de Pirámide.
 * @param {object} seg - transacción de seguro (respuesta de /seguros/venta-masiva/emitir)
 * @param {object} ctx - { operatorName, paperSize, feedLines, cutPaper }
 */
export function buildSeguroBytes(seg, ctx = {}) {
    const { operatorName = "", paperSize = "58mm", feedLines = 3, cutPaper = true } = ctx;
    const p = new EscPos(paperSize).init();
    const W = p.cols;
    const HR = "-".repeat(W);
    const producto = seg.product_label || seg.codprod || (seg.request_payload && seg.request_payload.codprod) || "-";

    header(p, []);
    p.style({ bold: true }).line("SEGUROS PIRAMIDE").style({});
    p.line("Venta Masiva");
    if (operatorName) p.line(operatorName);
    p.line(formatDateVE(seg.created_at));
    p.line(HR);

    p.alignLeft();
    p.style({ bold: true }).pair("PRODUCTO", String(producto)).style({});
    p.pair("Cedula/RIF", seg.customer_id || "-");
    p.pair("Cliente", seg.customer_name || "-");
    p.line(HR);
    p.style({ bold: true, dheight: true }).pair("MONTO Bs", fmtMoney(seg.monto_bs)).style({});
    p.pair("Total USD", "$ " + fmtMoney(seg.monto_usd));
    p.line(HR);

    p.alignCenter().line("SERIAL DE ACTIVACION");
    p.code(seg.policy_number || "-");
    p.line(HR);

    if (seg.activation_url) {
        p.alignCenter().style({ bold: true }).line("ACTIVE SU SEGURO").style({});
        qrBlock(p, seg.activation_url);
        p.line(HR);
    }

    footer(p, ["Escanee el QR para activar", "Validez sujeta a activacion en Piramide", "Conserve este comprobante"], feedLines, cutPaper);
    return p.build();
}

/** Imprime un comprobante de Venta Masiva de Seguros Pirámide por Bluetooth (reconecta o empareja). */
export async function printSeguroViaBluetooth(seg, ctx = {}) {
    try {
        const conn = await getPrinterConnection();
        const bytes = buildSeguroBytes(seg, ctx);
        await writeChunks(conn.characteristic, bytes);
        return { ok: true, device: conn.device };
    } catch (e) {
        return { ok: false, error: e?.message || String(e) };
    }
}

// ---------- Bluetooth: emparejar, conectar, escribir ----------

async function findWritableCharacteristic(server) {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
        const chars = await svc.getCharacteristics();
        for (const c of chars) {
            if (c.properties.writeWithoutResponse || c.properties.write) {
                return c;
            }
        }
    }
    throw new Error("La impresora no expone una característica de escritura BLE.");
}

// Conexión persistente en memoria: evita reabrir el selector nativo en cada impresión.
let cachedDevice = null;
let cachedChar = null;

function cacheConnection(device, characteristic) {
    if (cachedDevice !== device) {
        device.addEventListener("gattserverdisconnected", () => { cachedChar = null; });
    }
    cachedDevice = device;
    cachedChar = characteristic;
    return { device, characteristic };
}

async function connectAndGetChar(device) {
    if (device.gatt.connected) {
        // Ya conectada; recuperar característica
        return await findWritableCharacteristic(device.gatt);
    }
    const server = await device.gatt.connect();
    return await findWritableCharacteristic(server);
}

export async function pairPrinter() {
    if (!isWebBluetoothAvailable()) throw new Error("Web Bluetooth no está disponible en este navegador.");
    const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: KNOWN_SERVICES,
    });
    if (!device) throw new Error("No se seleccionó ningún dispositivo.");
    const char = await connectAndGetChar(device);
    localStorage.setItem(LS_KEY, JSON.stringify({ id: device.id, name: device.name || "Impresora Bluetooth" }));
    return cacheConnection(device, char);
}

/** Conexión activa sin diálogo: memoria → reconexión GATT silenciosa → getDevices(). */
async function getSilentConnection() {
    if (cachedDevice) {
        try {
            if (cachedDevice.gatt.connected && cachedChar) return { device: cachedDevice, characteristic: cachedChar };
            return cacheConnection(cachedDevice, await connectAndGetChar(cachedDevice));
        } catch { /* reintento vía getDevices */ }
    }
    return await reconnectSaved();
}

/** Obtiene la conexión; solo abre el selector nativo si no hay dispositivo reutilizable. */
async function getPrinterConnection() {
    if (!isWebBluetoothAvailable()) throw new Error("Web Bluetooth no disponible");
    const conn = await getSilentConnection();
    if (conn) return conn;
    // requestDevice exige gesto de usuario: en auto-impresión no hay, avisamos en vez de fallar críptico.
    if (navigator.userActivation && !navigator.userActivation.isActive) {
        throw new Error("Impresora Bluetooth no vinculada. Pulsa el botón Bluetooth para emparejarla.");
    }
    return await pairPrinter();
}

/** Usado en tests/diagnóstico: indica si hay un dispositivo en memoria. */
export function hasCachedPrinter() {
    return !!cachedDevice;
}

export function getSavedPrinter() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export function forgetPrinter() {
    localStorage.removeItem(LS_KEY);
    try { if (cachedDevice?.gatt?.connected) cachedDevice.gatt.disconnect(); } catch { /* noop */ }
    cachedDevice = null;
    cachedChar = null;
}

async function reconnectSaved() {
    if (!isWebBluetoothAvailable() || !navigator.bluetooth.getDevices) return null;
    const saved = getSavedPrinter();
    if (!saved) return null;
    try {
        const devices = await navigator.bluetooth.getDevices();
        const dev = devices.find((d) => d.id === saved.id);
        if (!dev) return null;
        return cacheConnection(dev, await connectAndGetChar(dev));
    } catch {
        return null;
    }
}

async function writeChunks(characteristic, bytes, chunkSize = 180) {
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const slice = bytes.subarray(i, i + chunkSize);
        if (characteristic.properties.writeWithoutResponse) {
            // Preferible en impresoras baratas; menor latencia
            await characteristic.writeValueWithoutResponse(slice);
        } else {
            await (characteristic.writeValueWithResponse ? characteristic.writeValueWithResponse(slice) : characteristic.writeValue(slice));
        }
        // Pequeña pausa: da tiempo al buffer del printer
        await new Promise((r) => setTimeout(r, 20));
    }
}

/**
 * Imprime un ticket. Si no hay conexión previa o el usuario no ha emparejado,
 * abre el diálogo de selección. Devuelve `{ ok, error }`.
 */
export async function printTicketViaBluetooth(ticket, ctx = {}) {
    try {
        const conn = await getPrinterConnection();
        const bytes = buildTicketBytes(ticket, ctx);
        await writeChunks(conn.characteristic, bytes);
        return { ok: true, device: conn.device };
    } catch (e) {
        return { ok: false, error: e?.message || String(e) };
    }
}
